import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { apiClient } from '@/api/axiosClient';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import type { RoleDto } from '@/types/administration';

export default function RolesPermissionsMatrix() {
  const { t } = useTranslation('userManagement');

  const { data: roles = [] } = useQuery({
    queryKey: ['roles'],
    queryFn: async () => (await apiClient.get<RoleDto[]>('/roles')).data,
  });

  const { data: processConfiguration } = useProcessConfiguration();
  // LabelTemplate.* covers both Label Designer and GRN Label Print — both are now gated on
  // barcodeMandatory in the left nav (enableGrnLabelPrinting always mirrors barcodeMandatory
  // server-side, so checking either is equivalent), so fully hidden here too (not just flagged)
  // to match, same "hide, don't just flag" treatment as every other config-gated module below.
  const labelTemplateDisabled = processConfiguration ? !processConfiguration.barcodeMandatory : false;
  // productionModuleEnabled is the common master for the WHOLE Production module — off hides
  // everything below regardless of any individual sub-flag's own state.
  const productionModuleDisabled = processConfiguration ? !processConfiguration.productionModuleEnabled : false;
  // Material Request (and Picking, downstream of it) is independent of Production Plan — a
  // company can choose either, both, or neither — fully hidden (not just flagged) when its own
  // switch or the common module master is off, matching the left nav's gate for the same two
  // screens.
  const materialRequestDisabled = processConfiguration ? !processConfiguration.materialRequestEnabled : false;
  const productionDisabled = processConfiguration ? !processConfiguration.productionEnabled : false;
  const materialRequestOrPickingHidden = materialRequestDisabled || productionModuleDisabled;
  // Job Work — its own module master switch, off by default; same fully-hidden treatment as the
  // other config-gated modules here.
  const jobWorkDisabled = processConfiguration ? !processConfiguration.jobWorkEnabled : false;
  // Adjustments — each an independent master switch, fully hidden (not just flagged) to match the
  // left nav, same treatment as Production above.
  const stockAdjustmentDisabled = processConfiguration ? !processConfiguration.stockAdjustmentEnabled : false;
  const stockTransferDisabled = processConfiguration ? !processConfiguration.stockTransferEnabled : false;
  const cycleCountDisabled = processConfiguration ? !processConfiguration.cycleCountEnabled : false;
  const returnNoteDisabled = processConfiguration ? !processConfiguration.returnNoteEnabled : false;
  // Packing Label Print reuses the same Packing.View/.Execute permission codes as the Packing
  // screen itself (no separate PackingLabel permission exists), so there's only ever one "Packing"
  // chip-group here to hide — it goes when the Packing master switch is off. There's no way to
  // independently hide just the Label Print half of it from this screen; that finer-grained
  // dependency (packingLabelEnabled) is enforced in the left nav instead (see AppShell.tsx).
  const packingDisabled = processConfiguration ? !processConfiguration.packingEnabled : false;
  // GateExit has its own permission module (unlike Packing Label Print), so it can be hidden
  // independently here, same fully-hidden treatment as Production/Adjustments above.
  const gateExitDisabled = processConfiguration ? !processConfiguration.gateExitEnabled : false;
  // Client (3PL) has its own permission module too, same fully-hidden treatment.
  const thirdPartyLogisticsDisabled = processConfiguration ? !processConfiguration.thirdPartyLogisticsEnabled : false;
  // Billing, ClientContract, and ClientPortal are each independent sub-switches of 3PL (Process
  // Configuration → Third-Party Logistics card) — hidden when EITHER 3PL itself or their own
  // sub-flag is off (mirrors AppShell.tsx's configKey AND-semantics for Billing/ClientPortal).
  // ClientContract is the one exception: it only needs 3PL, not BillingEnabled too — a company can
  // set up Clients' rate cards ahead of time before separately switching invoicing on (mirrors
  // MastersPage.tsx's clientContracts tab and ClientContractService.EnsureThirdPartyLogisticsEnabledAsync).
  const billingDisabled = thirdPartyLogisticsDisabled || (processConfiguration ? !processConfiguration.billingEnabled : false);
  const clientContractDisabled = thirdPartyLogisticsDisabled;
  const clientPortalDisabled = thirdPartyLogisticsDisabled || (processConfiguration ? !processConfiguration.clientPortalEnabled : false);
  // Inverse direction — Purchase Request/Order, Production, and BOM stop being relevant once a
  // company switches into 3PL mode (mirrors AppShell.tsx's hiddenWhenConfigKey for the same
  // screens): a 3PL warehouse receives client-owned goods via GRN rather than purchasing its own
  // inventory, and typically doesn't manufacture.
  const thirdPartyLogisticsEnabled = Boolean(processConfiguration?.thirdPartyLogisticsEnabled);
  const purchaseRequestHiddenFor3pl = thirdPartyLogisticsEnabled;
  const purchaseOrderHiddenFor3pl = thirdPartyLogisticsEnabled;
  // BOM has two independent consumers, either of which is reason enough to keep its permissions
  // grantable (mirrors MastersPage.tsx's bom tab configPredicate exactly): Production Plan (hidden
  // again under 3PL — a 3PL warehouse doesn't manufacture), or Job Work's "BOM-Based Sending",
  // which explodes the output material's Active BOM to decide what goes to the vendor and is NOT
  // 3PL-hidden. Without the second arm, a Job-Work-in-BOM-mode company couldn't grant Bom.View at
  // all, so the BOM master stayed unreachable for every role but a permission-bypassing admin.
  const jobWorkBomSendingEnabled = Boolean(processConfiguration?.jobWorkEnabled && processConfiguration?.jobWorkBomEnabled);
  const bomHidden = !((!thirdPartyLogisticsEnabled && !productionDisabled) || jobWorkBomSendingEnabled);
  const productionHiddenForModuleOrClient = productionDisabled || thirdPartyLogisticsEnabled;

  return (
    <GridContainer spacing={2} columns={3}>
      {roles.map((role) => {
        const byModule = new Map<string, string[]>();
        for (const code of role.permissionCodes) {
          const [module, action] = code.split('.');
          // Process Configuration is now a SuperAdmin-only, per-company concern (managed from the
          // Companies console), not something granted to a company's own roles — see
          // CompanyProcessConfigurationPage. Kept out of this display regardless of what any role
          // still happens to hold in the permission table.
          if (module === 'ProcessConfiguration') continue;
          if (module === 'Production' && productionHiddenForModuleOrClient) continue;
          if (module === 'JobWork' && jobWorkDisabled) continue;
          if ((module === 'MaterialRequest' || module === 'Picking') && materialRequestOrPickingHidden) continue;
          if (module === 'StockAdjustment' && stockAdjustmentDisabled) continue;
          if (module === 'StockTransfer' && stockTransferDisabled) continue;
          if (module === 'CycleCount' && cycleCountDisabled) continue;
          if (module === 'ReturnNote' && returnNoteDisabled) continue;
          if (module === 'Packing' && packingDisabled) continue;
          if (module === 'GateExit' && gateExitDisabled) continue;
          if (module === 'Client' && thirdPartyLogisticsDisabled) continue;
          if (module === 'PurchaseRequest' && purchaseRequestHiddenFor3pl) continue;
          if (module === 'PurchaseOrder' && purchaseOrderHiddenFor3pl) continue;
          if (module === 'Bom' && bomHidden) continue;
          if (module === 'ClientContract' && clientContractDisabled) continue;
          if (module === 'Billing' && billingDisabled) continue;
          if (module.startsWith('ClientPortal') && clientPortalDisabled) continue;
          if (module === 'LabelTemplate' && labelTemplateDisabled) continue;
          byModule.set(module, [...(byModule.get(module) ?? []), action]);
        }

        return (
          <GridItem key={role.id} xs={3} sm={1}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="h4">{role.name}</Typography>
                  {role.isSystemRole ? <Chip label={t('systemRole')} size="small" color="primary" /> : undefined}
                </Stack>
                {role.description ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {role.description}
                  </Typography>
                ) : undefined}
                <Stack spacing={1}>
                  {[...byModule.entries()].map(([module, actions]) => (
                    <Stack key={module} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 120 }}>
                        {module}
                      </Typography>
                      {actions.map((a) => (
                        <Chip key={a} label={a} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </GridItem>
        );
      })}
    </GridContainer>
  );
}
